import React, { useEffect, useMemo, useCallback } from 'react';
import { Box, useTheme, useMediaQuery } from '@mui/material';
import SidebarMenuItems from './SidebarMenuItems';
import SidebarBrand from './SidebarBrand';
import AssistantNav, { OverviewNavItem } from './AssistantNav';
import { List, Divider } from '@mui/material';
import Diversity3OutlinedIcon from '@mui/icons-material/Diversity3Outlined';
import EventNoteOutlinedIcon from '@mui/icons-material/EventNoteOutlined';
import BallotOutlinedIcon from '@mui/icons-material/BallotOutlined';
import { useAuth } from '../../context/AuthContext';
import { SECTION_ROLES } from '../../config/sectionAccess';


const Sidebar = ({ mobileOpen }) => {
    const { user, isLoading } = useAuth();
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up('sm'));
    const sidebarWidth = isDesktop ? 260 : 240;

    // Borna central support agents get their own section; they usually have
    // no institute staff role, so the regular menus filter out for them.
    const isBornaSupport = Boolean(user?.is_superuser || user?.is_support_staff);
    const menuItems = useMemo(() => {
        const items = [
        {
            id: 'definitions',
            title: 'پیکربندی‌ها',
            roles: ['manager'],
            children: [
                { id: 'institute-info', title: 'اطلاعات موسسه', roles: SECTION_ROLES['institute-info'] },
                { id: 'emails', title: 'مدیریت ارسال ایمیل', roles: SECTION_ROLES['emails'] },
                { id: 'daily-report', title: 'تنظیمات گزارش روزانه', roles: SECTION_ROLES['daily-report'] }
            ]
        },
        {
            id: 'programs',
            title: 'عملیات سامانه',
            roles: ['secretary'],
            children: [
                {
                    id: 'users',
                    title: 'کاربران',
                    roles: ['manager'],
                    icon: <Diversity3OutlinedIcon fontSize="1rem" />,
                    children: [
                        { id: 'staff-list', title: 'کارمندان', roles: SECTION_ROLES['staff-list'] },
                        { id: 'teacher-list', title: 'آموزگاران', roles: SECTION_ROLES['teacher-list'] },
                        { id: 'student-list', title: 'هنرجویان', roles: SECTION_ROLES['student-list'] }
                    ]
                },
                {
                    id: 'student-courses',
                    title: 'برنامه‌های آموزشی',
                    icon: <BallotOutlinedIcon fontSize="1rem" />,
                    children: [
                        { id: 'courses-list', title: 'فهرست دوره ها', roles: SECTION_ROLES['courses-list'] },
                        { id: 'teacher-schedule', title: 'برنامه آموزگاران', roles: SECTION_ROLES['teacher-schedule'] },
                        { id: 'student-schedule', title: 'برنامه هنرجویان', roles: SECTION_ROLES['student-schedule'] },
                    ]
                },
                {
                    id: 'program-list',
                    title: 'فرآیندهای جاری',
                    roles: ['secretary'],
                    icon: <EventNoteOutlinedIcon fontSize="1rem" />,
                    children: [
                        { id: 'student-card', title: 'حضور و غیاب', roles: SECTION_ROLES['student-card'] },
                        { id: 'attendance-review', title: 'بازبینی حضور و غیاب', roles: SECTION_ROLES['attendance-review'] },
                    ]
                }
            ]
        },
        {
            id: 'analytics',
            title: 'آمار و ارقام',
            roles: ['manager'],
            children: [
                { id: 'statistics', title: 'آمار سامانه', roles: SECTION_ROLES['statistics'] },
                { id: 'classroom-info-graph', title: 'داده نگار کلاس ها', roles: SECTION_ROLES['classroom-info-graph'] },
                // { id: 'advanced-financial-analytics', title: 'تحلیل مالی پیشرفته', roles: ['accountant', 'secretary'] }, // TODO: Will be implemented in the future
            ]
        },
        {
            id: 'accounting',
            title: 'حسابداری',
            roles: ['accountant', 'teacher', 'student'],
            children: [
                { id: 'institution-accounting', title: ' آموزشگاه', roles: SECTION_ROLES['institution-accounting'] },
                { id: 'operation-browser', title: ' مرور عملیات‌ها', roles: SECTION_ROLES['operation-browser'] },
                { id: 'expense-register', title: ' ثبت هزینه‌ها', roles: SECTION_ROLES['expense-register'] },
                { id: 'teacher-accounting', title: ' تسویه حقوق', roles: SECTION_ROLES['teacher-accounting'] },
                { id: 'teacher-salary-management', title: ' حقوق های ماهیانه', roles: SECTION_ROLES['teacher-salary-management'] },
                { id: 'student-accounting', title: ' هنرجویان', roles: SECTION_ROLES['student-accounting'] },
                { id: 'cashbox-reconciliation', title: ' تطبیق صندوق', roles: SECTION_ROLES['cashbox-reconciliation'] },
                { id: 'bank-reconciliation', title: ' تطبیق بانکی', roles: SECTION_ROLES['bank-reconciliation'] }
            ]
        }
        ];
        if (isBornaSupport) {
            items.push({
                id: 'borna-support',
                title: 'پشتیبانی برنا',
                children: [
                    { id: 'staff-support-panel', title: 'گفتگوهای پشتیبانی' },
                ],
            });
        }
        return items;
    }, [isBornaSupport]);

    // roles from context
    const userRoles = useMemo(() => {
        const roles =
            Array.isArray(user?.roles) ? user.roles.map(r => String(r).toLowerCase()) : [];
        const staffName = user?.staff_role?.name;
        if (staffName) roles.push(String(staffName).toLowerCase());
        return Array.from(new Set(roles));
    }, [user]);

    // role check
    const intersects = (itemRoles, roles) => {
        if (!itemRoles || itemRoles.length === 0) return true;
        return roles.some(r => itemRoles.includes(r));
    };

    // recursive filter with logging (summarized)
    const filterByRoles = useCallback((items, roles, path = '') => {
        const isManager = roles.includes('manager');
        return items.reduce((acc, item) => {
            const here = path ? `${path}/${item.id}` : `/${item.id}`;
            const includeItem = isManager || intersects(item.roles, roles);

            let next = { ...item };
            if (next.children) {
                // Containers exist only to hold leaves: drop the group when
                // every child filtered out (e.g. support staff with no role
                // used to see an empty "برنامه‌های آموزشی").
                next.children = filterByRoles(next.children, roles, here);
                if (next.children.length > 0) {
                    acc.push(next);
                }
                return acc;
            }

            if (includeItem) {
                acc.push(next);
            }
            return acc;
        }, []);
    }, []);

    const filteredMenu = useMemo(() => {
        if (isLoading) return [];
        if (!user) return [];
        return filterByRoles(menuItems, userRoles);
    }, [isLoading, user, userRoles, filterByRoles, menuItems]);



    return (
        <Box
            sx={{
                transform: isDesktop ? 'translateX(0)' : (mobileOpen ? 'translateX(0%)' : 'translateX(100%)'),
                transition: 'transform 0.3s ease-in-out',
                width: sidebarWidth,
                height: isDesktop ? '100vh' : 'calc(100vh - 4rem)',
                display: 'flex',
                flexDirection: 'column',
                bgcolor: theme.palette.background.chrome,
                borderLeft: `1px solid ${theme.palette.border.main}`,
                zIndex: t => t.zIndex.drawer + 100
            }}
        >
            {/* Brand stays pinned at the top */}
            <Box sx={{ flexShrink: 0, px: '1rem' }}>
                <SidebarBrand />
            </Box>

            {/* Only the menu list scrolls */}
            <Box
                sx={{
                    flex: 1,
                    minHeight: 0,
                    overflowY: 'auto',
                    px: '1rem',
                    pb: '2rem',
                    // slim, theme-aware scrollbar
                    scrollbarWidth: 'thin',
                    scrollbarColor: `${theme.palette.border.main} transparent`,
                    '&::-webkit-scrollbar': { width: '6px' },
                    '&::-webkit-scrollbar-thumb': {
                        backgroundColor: theme.palette.border.main,
                        borderRadius: '3px',
                    },
                    '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
                }}
            >
                {isLoading ? (
                    <div style={{ padding: '1rem', opacity: 0.7 }}>در حال بارگذاری…</div>
                ) : (
                    <>
                        {/* Overview + the assistant sit above the work groups
                            (the assistant used to live under «آمار و ارقام»). */}
                        {user && (
                            <List aria-label="دسترسی سریع" sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                <Box component="li" sx={{ listStyle: 'none' }}>
                                    <OverviewNavItem />
                                </Box>
                                <AssistantNav />
                            </List>
                        )}
                        {user && filteredMenu.length > 0 && (
                            <Divider sx={{ mt: 2, mx: 1, borderColor: theme.palette.border.main, opacity: 0.4 }} />
                        )}
                        <SidebarMenuItems menuItems={filteredMenu} />
                    </>
                )}
            </Box>
        </Box>
    );
};

export default Sidebar;
